import { Typography, message, Button, Row, Col, Space, Table, Switch, Modal, Form, Input, DatePicker, Avatar, Card } from "antd";
import PageWrapper from "../utils/PageWrapper";
import { useState, useEffect } from "react";
import { useAuth } from "../layout/auth";
import axios from "axios";
import moment from "moment";
import "moment/locale/es";
import PageTitle from "../utils/PageTitle";
import { User, Plus, Mail, Users, Edit, Wrench } from "lucide-react";

moment.locale("es-mx");

export default function Perfil() {
  const [userData, setUserData] = useState({});
  const [equipments, setEquipments] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentEquipment, setCurrentEquipment] = useState(null);
  const [search, setSearch] = useState(""); // New state for search term
  const [filteredEquipments, setFilteredEquipments] = useState([]); // New state for filtered equipments

  const [form] = Form.useForm();

  const auth = useAuth();

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/usuarios/${auth.UID}`)
      .then((res) => {
        setUserData(res.data);
      })
      .catch((err) => {
        message.error("Error fetching user data");
      });

    axios
      .get(`${process.env.REACT_APP_API_URL}/equipos`, { params: { u: auth.UID } })
      .then((res) => {
        setEquipments(res.data);
        setFilteredEquipments(res.data); // Initialize filtered equipments
      })
      .catch((err) => {
        message.error("Error fetching equipment data");
      });
  }, []);

  useEffect(() => {
    if (search) {
      const filtered = equipments.filter((equipment) => {
        return (
          equipment?.NombreEquipo?.toLowerCase().includes(search.toLowerCase()) ||
          equipment?.FechaUltimaCalibracion?.toLowerCase().includes(search.toLowerCase()) ||
          equipment?.FechaProximaCalibracion?.toLowerCase().includes(search.toLowerCase())
        );
      });
      setFilteredEquipments(filtered);
    } else {
      setFilteredEquipments(equipments);
    }
  }, [search, equipments]);

  const handleAddEquipment = () => {
    setIsModalVisible(true);
    setIsEdit(false); 
    setCurrentEquipment(null); 
  };

  const handleEditEquipment = (record) => {
    setIsModalVisible(true);
    setIsEdit(true); 
    setCurrentEquipment(record); 
  };

  useEffect(() => {
    if (isEdit && currentEquipment) {
      form.setFieldsValue({
        NombreEquipo: currentEquipment.NombreEquipo,
        FechaUltimaCalibracion: moment(currentEquipment.FechaUltimaCalibracion, "YYYY-MM-DD"),
        FechaProximaCalibracion: moment(currentEquipment.FechaProximaCalibracion, "YYYY-MM-DD"),
        Active: currentEquipment.Active,
      });
    } else {
      form.resetFields();
    }
  }, [isEdit, currentEquipment, form]);

  const handleSaveEquipment = (values) => {
    const requestData = {
      NombreEquipo: values.NombreEquipo,
      FechaUltimaCalibracion: values.FechaUltimaCalibracion.format("YYYY-MM-DD"),
      FechaProximaCalibracion: values.FechaProximaCalibracion.format("YYYY-MM-DD"),
      Active: values.Active,
    };

    if (isEdit && currentEquipment) {
      axios
        .put(`${process.env.REACT_APP_API_URL}/equipos/${currentEquipment.EquipoID}?u=${auth.UID}`, requestData)
        .then(() => {
          message.success("Equipo actualizado exitosamente");
          axios.get(`${process.env.REACT_APP_API_URL}/equipos`, { params: { u: auth.UID } }).then((res) => {
            setEquipments(res.data);
          });
          setIsModalVisible(false);
        })
        .catch(() => {
          message.error("Error al actualizar el equipo");
        });
    } else {
      axios
        .post(`${process.env.REACT_APP_API_URL}/equipos?u=${auth.UID}`, requestData)
        .then(() => {
          message.success("Equipo añadido exitosamente");
          axios.get(`${process.env.REACT_APP_API_URL}/equipos`, { params: { u: auth.UID } }).then((res) => {
            setEquipments(res.data);
          });
          setIsModalVisible(false);
        })
        .catch(() => {
          message.error("Error al añadir el equipo");
        });
    }
  };

  const handleToggleActive = (record) => {
    const updatedRecord = {
      ...record, // Spread the original record
      Active: !record.Active, // Toggle the active state
    };

    axios
      .put(`${process.env.REACT_APP_API_URL}/equipos/${record.EquipoID}?u=${auth.UID}`, updatedRecord) // Send the updated record
      .then(() => {
        message.success("Estado del equipo actualizado exitosamente");
        axios.get(`${process.env.REACT_APP_API_URL}/equipos`, { params: { u: auth.UID } }).then((res) => {
          setEquipments(res.data);
        });
      })
      .catch(() => {
        message.error("Error al actualizar el estado del equipo");
      });
  };

  const equipmentColumns = [
    {
      title: "Nombre del Equipo",
      dataIndex: "NombreEquipo",
      key: "NombreEquipo",
    },
    {
      title: "Fecha Última Calibración",
      dataIndex: "FechaUltimaCalibracion",
      key: "FechaUltimaCalibracion",
      render: (date) => (
        <div>
          <div>{moment(date).format("DD/MM/YYYY")}</div>
          <div style={{ fontSize: "12px", color: "#888" }}>{moment(date).fromNow()}</div>
        </div>
      ),
    },
    {
      title: "Fecha Próxima Calibración",
      dataIndex: "FechaProximaCalibracion",
      key: "FechaProximaCalibracion",
      render: (date) => (
        <div>
          <div>{moment(date).format("DD/MM/YYYY")}</div>
          <div style={{ fontSize: "12px", color: "#888" }}>{moment(date).fromNow()}</div>
        </div>
      ),
    },
    {
      title: "Activo",
      render: (_, record) => (
        <Switch
          checked={record.Active}
          onChange={() => handleToggleActive(record)} // Send the whole record here
        />
      ),
    },
    {
      title: "Acciones",
      render: (_, record) => (
        <div>
          <Button type="primary" icon={<Edit size={18} strokeWidth={1.5} />} onClick={() => handleEditEquipment(record)} />
        </div>
      ),
    },
  ];

  return (
    <div style={{ minHeight: "100vh" }}>
      <PageWrapper className="dashboard-main-card">
        <PageTitle icon={<User />} title="Perfil de Usuario" />

        {/* User Info */}
        <div
          style={{
            padding: "16px", /* Reduced padding */
            background: "rgba(249, 250, 251, 0.8)",
            borderRadius: "12px",
            border: "1px solid rgba(229, 231, 235, 0.6)",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.05)",
            marginBottom: "24px",
          }}
        >
          <Row gutter={[16, 16]} align="middle">
            <Col>
              <Avatar size={64} icon={<User size={32} strokeWidth={1.5} />} style={{ backgroundColor: "#1890ff" }} />
            </Col>
            <Col flex="auto" style={{ textAlign: "left" }}>
              <Typography.Title level={3} style={{ margin: 0, color: "#1f2937" }}>
                {userData[0]?.Nombre} {userData[0]?.Apellido}
              </Typography.Title>
              <Typography.Text style={{ fontSize: "14px", color: "#595959" }}>
                <Mail size={18} strokeWidth={1.5} style={{ marginRight: "8px", color: "#1890ff", verticalAlign: "middle" }} />
                {userData[0]?.Email.toUpperCase() || <span style={{ color: "#888" }}>N/D</span>}
              </Typography.Text>
              <br />
              <Typography.Text style={{ fontSize: "14px", color: "#595959" }}>
                <Users size={18} strokeWidth={1.5} style={{ marginRight: "8px", color: "#1890ff", verticalAlign: "middle" }} />
                {userData[0]?.Rol.toUpperCase() || <span style={{ color: "#888" }}>N/D</span>}
              </Typography.Text>
            </Col>
          </Row>
        </div>

        {/* Equipos Section */}
        <div style={{ marginBottom: "24px" }}>
          <Typography.Title level={4} style={{ marginBottom: "16px", color: "#595959" }}>
            <Wrench size={24} strokeWidth={1.5} style={{ marginRight: "8px", color: "#1890ff", verticalAlign: "middle" }} />
            Equipos
          </Typography.Title>
          <div
            style={{
              padding: "16px",
              background: "rgba(249, 250, 251, 0.8)",
              borderRadius: "12px",
              border: "1px solid rgba(229, 231, 235, 0.6)",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.05)",
              marginBottom: "24px",
            }}
          >
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Input.Search
                  placeholder="Buscar equipo"
                  onSearch={(value) => setSearch(value)}
                  enterButton="Buscar"
                  size="large"
                  allowClear
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ textAlign: "right" }}>
                <Space>
                  <Button
                    type="primary"
                    icon={<Plus size={18} strokeWidth={1.5} />} 
                    size="large"
                    onClick={handleAddEquipment}
                    style={{
                      borderRadius: "8px",
                      height: "40px",
                    }}
                  >
                    Agregar equipo
                  </Button>
                </Space>
              </Col>
            </Row>
          </div>

          <Card
            style={{
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
            bodyStyle={{ padding: "20px" }}
          >
            <Table columns={equipmentColumns} dataSource={filteredEquipments} rowKey="id" pagination={false} />
          </Card>
        </div>

        <Modal
          title={isEdit ? "Editar Equipo" : "Agregar Equipo"}
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={null}
          headStyle={{
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
            backgroundColor: "#1890ff",
          }}
          titleStyle={{ color: "#fff" }}
          bodyStyle={{ borderRadius: "0 0 20px 20px" }}
        >
          <Form layout="vertical" onFinish={handleSaveEquipment} form={form} name="equipo-form">
            <Form.Item
              name="NombreEquipo"
              label="Nombre del Equipo"
              rules={[{ required: true, message: "Por favor ingresa el nombre del equipo" }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="FechaUltimaCalibracion"
              label="Fecha Última Calibración"
              rules={[{ required: true, message: "Por favor ingresa la fecha de la última calibración" }]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
              name="FechaProximaCalibracion"
              label="Fecha Próxima Calibración"
              rules={[{ required: true, message: "Por favor ingresa la fecha de la próxima calibración" }]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item name="Active" label="Activo" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Space>
              <Button type="primary" htmlType="submit">
                Guardar
              </Button>
              <Button onClick={() => setIsModalVisible(false)}>Cancelar</Button>
            </Space>
          </Form>
        </Modal>
      </PageWrapper>
    </div>
  );
}
